import { cn } from "../../../lib/utils";
import type { AuditEvent } from "../../../models/audit/auditEvent";
import { formatClockTime } from "./auditDates";
import { AuditEventGlyph } from "./AuditEventGlyph";
import { buildEventContext } from "./eventContext";
import { resolveActor } from "./resolveActor";

interface AuditEventRailRowProps {
  event: AuditEvent;
  selected: boolean;
  onSelect: () => void;
  memberEmailById?: (userId: string) => string | undefined;
  columnNameById?: (columnId: string) => string | undefined;
  /** True on surfaces whose feed mixes tests (the workspace page): the
   * sub-line gains a right-aligned origin — the event's test name, or its
   * control name for control-level events. The session drawer leaves this
   * off; its whole feed shares one origin. */
  showOrigin?: boolean;
}

/**
 * One node on the audit timeline rail: a glyph disc sitting on the day
 * group's hairline, the event title, an `actor · context` sub-line, and the
 * clock time. Selecting a row drives the detail pane — the rail itself never
 * expands, so the chronology never reflows while you read.
 */
export function AuditEventRailRow({
  event,
  selected,
  onSelect,
  memberEmailById,
  columnNameById,
  showOrigin = false,
}: AuditEventRailRowProps) {
  const actor = resolveActor(event, memberEmailById);
  const context = buildEventContext(event, columnNameById);
  // Same containment breadcrumb as the pane footer: control › test — using
  // the *current* names when the feed joined them, so the list matches what
  // the rest of the app calls things today. The pane's stored names remain
  // the point-in-time record.
  const origin = showOrigin
    ? [
        event.currentControlName ?? event.controlName,
        event.currentTestName ?? event.testName,
      ]
        .filter((part): part is string => !!part && part.trim().length > 0)
        .join(" › ") || null
    : null;

  return (
    <button
      type="button"
      // Lets the page move DOM focus onto the row the arrow keys select, so
      // focus never strands on a previously clicked row.
      data-audit-event-id={event.id}
      onClick={onSelect}
      className={cn(
        "relative flex w-full items-start gap-3 rounded-md py-2.5 pl-2 pr-3 text-left transition-colors",
        // A clicked row keeps DOM focus while the arrow keys move the
        // selection on, so a click-painted ring would strand itself on a row
        // that is no longer current. `focus-visible` paints for keyboard
        // focus only; the current row is marked by its own fill and rule.
        "focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-stone-300",
        selected ? "bg-stone-100" : "hover:bg-stone-50",
      )}
    >
      {selected && (
        <span className="absolute inset-y-1.5 left-0 w-0.5 rounded-full bg-midship-orange" />
      )}
      <AuditEventGlyph
        eventType={event.eventType}
        className={cn(
          "relative z-10 mt-px",
          selected ? "border-stone-400" : "border-stone-200",
        )}
      />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[13px] font-medium text-stone-900">
          {event.title}
        </span>
        <span className="mt-0.5 block truncate text-xs text-stone-500">
          {actor}
          {context && ` · ${context}`}
        </span>
      </span>
      <span className="flex shrink-0 flex-col items-end pt-0.5">
        <span className="text-xs tabular-nums text-stone-400">
          {formatClockTime(new Date(event.createdAt))}
        </span>
        {origin && (
          <span className="mt-0.5 max-w-[240px] truncate text-[11px] text-stone-400">
            {origin}
          </span>
        )}
      </span>
    </button>
  );
}
